import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import {
  updateSettings,
  setError as setTradingError,
} from "../store/slices/tradingSlice";
import websocketService from "../services/websocket";
import useRemoveClosedOrders from "../hooks/useRemoveClosedOrders";

import PriceChart from "./PriceChart";
import { TradingPanel } from "widgets/traiding-panel";
import Orders from "./Orders";
import BottomPanel from "./BottomPanel";
import Modal from "./Modal";
import { FinanceModal } from "widgets/finance-modal";
import { Stats } from "features/user-stats";
import Help from "./Help";
import { StatusTiers } from "features/status-tiers";

import WalletIcon from "../assets/svg/wallet.svg";
import UserIcon from "../assets/svg/user.svg";
import StatisticsIcon from "../assets/svg/statistics.svg";
import HelpIcon from "../assets/svg/help.svg";
import StatusTiersIconSrc from "../assets/svg/status-tiers.svg";
import CaseIconSrc from "../assets/svg/case.svg";
import { StatusTiersIcon, CaseIcon } from "shared/ui/icons";
import { FinanceHistory } from "features/finance-history";
import { WheelRafflesNavButton } from "widgets/wheel-raffles";

import "./TradingDashboard.css";

const TradingDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const {
    selectedPair,
    currencyPairs,
    orders,
    orderHistory,
    prices,
    settings,
    error: tradingError,
  } = useSelector((state: RootState) => state.trading);
  const { isConnected } = useSelector((state: RootState) => state.websocket);

  // States for header trading buttons
  const [amount, setAmount] = useState<number>(10);
  const [duration, setDuration] = useState<number>(60); // seconds
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [orderInProgress, setOrderInProgress] = useState<"buy" | "sell" | null>(
    null,
  );

  // Effect for sending currency pair selection to the server
  useEffect(() => {
    if (selectedPair && isConnected) {
      websocketService.selectCurrencyPair(selectedPair);
    }
  }, [selectedPair, isConnected]);

  // State for tracking panel visibility
  const [isTradingPanelVisible, setIsTradingPanelVisible] = useState(true);

  // Handlers for hiding/showing panels
  const toggleTradingPanel = () => {
    setIsTradingPanelVisible(!isTradingPanelVisible);
  };

  const toggleSound = () => {
    dispatch(
      updateSettings({
        sound_enabled: !settings.sound_enabled,
      }),
    );
  };

  // Function to place order from header
  const handlePlaceOrder = async (direction: "buy" | "sell") => {
    if (!selectedPair || !isConnected || isPlacingOrder) return;

    setIsPlacingOrder(true);
    setOrderInProgress(direction);

    try {
      const wsDirection = direction === "buy" ? "call" : "put";
      websocketService.placeOrder(selectedPair, wsDirection, amount, duration);

      // Reset order status after completion
      setTimeout(() => {
        setIsPlacingOrder(false);
        setOrderInProgress(null);
      }, 1000);
    } catch (error) {
      setIsPlacingOrder(false);
      setOrderInProgress(null);
    }
  };

  const isOrderButtonDisabled =
    !selectedPair ||
    !isConnected ||
    isPlacingOrder ||
    (!settings.demo_balance && !settings.real_balance);
  const [activeTab, setActiveTab] = useState<
    "chart" | "profile" | "orders" | "help"
  >("chart");
  const activeOrders = orders.filter((order) => order.status === "open");
  const closedOrders = orderHistory.filter(
    (order) => order.status === "closed",
  );
  const [isOrdersOpen, setIsOrdersOpen] = useState(false);
  const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isStatusTiersModalOpen, setIsStatusTiersModalOpen] = useState(false);
  const [isFinanceHistoryModalOpen, setIsFinanceHistoryModalOpen] =
    useState(false);

  // Remove closed orders effect
  useRemoveClosedOrders();

  return (
    <div className="trading-dashboard">
      <div className="vertical-panel">
        <button className="button active" title="Trading">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M2.75 2C2.75 1.58579 2.41421 1.25 2 1.25C1.58579 1.25 1.25 1.58579 1.25 2V12.0574C1.24999 14.3658 1.24998 16.1748 1.43975 17.5863C1.63399 19.031 2.03933 20.1711 2.93414 21.0659C3.82895 21.9607 4.96897 22.366 6.41371 22.5603C7.82519 22.75 9.63423 22.75 11.9426 22.75H22C22.4142 22.75 22.75 22.4142 22.75 22C22.75 21.5858 22.4142 21.25 22 21.25H12C9.62178 21.25 7.91356 21.2484 6.61358 21.0736C5.33517 20.9018 4.56445 20.5749 3.9948 20.0052C3.42514 19.4355 3.09825 18.6648 2.92637 17.3864C2.75159 16.0864 2.75 14.3782 2.75 12V2Z" />
            <path d="M19.5875 7.46641C19.8451 7.14204 19.791 6.67026 19.4666 6.41267C19.1422 6.15508 18.6704 6.20921 18.4128 6.53359L15.2948 10.46C15.0496 10.7688 14.8887 10.9708 14.7561 11.1162C14.6265 11.2585 14.5657 11.2989 14.538 11.3137C14.3272 11.4264 14.0754 11.4319 13.8599 11.3285C13.8316 11.3149 13.7691 11.2772 13.6333 11.1407C13.4946 11.0011 13.3251 10.8063 13.0666 10.5085L13.0505 10.4898C12.8126 10.2157 12.6098 9.98186 12.4308 9.80184C12.2448 9.6147 12.0414 9.4401 11.7894 9.31918C11.143 9.00898 10.3875 9.02541 9.75518 9.36342C9.50872 9.49518 9.31307 9.67845 9.13536 9.87351C8.96441 10.0612 8.77192 10.3036 8.54619 10.5878L5.41267 14.5336C5.15508 14.8579 5.20921 15.3297 5.53358 15.5873C5.85795 15.8449 6.32973 15.7908 6.58733 15.4664L9.70551 11.54C9.95077 11.2311 10.1116 11.0292 10.2442 10.8837C10.3738 10.7414 10.4347 10.7011 10.4623 10.6863C10.6731 10.5736 10.925 10.5681 11.1404 10.6715C11.1687 10.6851 11.2313 10.7228 11.367 10.8593C11.5057 10.9989 11.6752 11.1936 11.9337 11.4915L11.9498 11.5101C12.1877 11.7843 12.3905 12.0181 12.5695 12.1981C12.7555 12.3853 12.9589 12.5599 13.2109 12.6808C13.8573 12.991 14.6129 12.9746 15.2452 12.6365C15.4916 12.5048 15.6873 12.3215 15.865 12.1264C16.0359 11.9388 16.2284 11.6964 16.4541 11.4121L19.5875 7.46641Z" />
            <defs>
              <linearGradient
                id="gradient"
                x1="1.25"
                y1="12"
                x2="22.75"
                y2="12"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#00DAEE" />
                <stop offset="1" stopColor="#00FA95" />
              </linearGradient>
            </defs>
          </svg>
        </button>

        <button
          className="button"
          title="Finance"
          onClick={() => setIsFinanceModalOpen(true)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75ZM1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12ZM12 5.25C12.4142 5.25 12.75 5.58579 12.75 6V6.31673C14.3804 6.60867 15.75 7.83361 15.75 9.5C15.75 9.91421 15.4142 10.25 15 10.25C14.5858 10.25 14.25 9.91421 14.25 9.5C14.25 8.82154 13.6859 8.10339 12.75 7.84748V11.3167C14.3804 11.6087 15.75 12.8336 15.75 14.5C15.75 16.1664 14.3804 17.3913 12.75 17.6833V18C12.75 18.4142 12.4142 18.75 12 18.75C11.5858 18.75 11.25 18.4142 11.25 18V17.6833C9.61957 17.3913 8.25 16.1664 8.25 14.5C8.25 14.0858 8.58579 13.75 9 13.75C9.41421 13.75 9.75 14.0858 9.75 14.5C9.75 15.1785 10.3141 15.8966 11.25 16.1525V12.6833C9.61957 12.3913 8.25 11.1664 8.25 9.5C8.25 7.83361 9.61957 6.60867 11.25 6.31673V6C11.25 5.58579 11.5858 5.25 12 5.25ZM11.25 7.84748C10.3141 8.10339 9.75 8.82154 9.75 9.5C9.75 10.1785 10.3141 10.8966 11.25 11.1525V7.84748ZM12.75 12.8475V16.1525C13.6859 15.8966 14.25 15.1785 14.25 14.5C14.25 13.8215 13.6859 13.1034 12.75 12.8475Z"
            />
          </svg>
        </button>

        <Modal
          isOpen={isFinanceModalOpen}
          onClose={() => setIsFinanceModalOpen(false)}
          icon={WalletIcon}
          title="Finance"
        >
          <FinanceModal onClose={() => setIsFinanceModalOpen(false)} />
        </Modal>

        <button
          className="button"
          title="Profile Statistics"
          onClick={() => setIsStatsModalOpen(true)}
        >
          <svg className="icon" width="24" height="24">
            <use href={StatisticsIcon} />
          </svg>
        </button>

        <Modal
          isOpen={isStatsModalOpen}
          onClose={() => setIsStatsModalOpen(false)}
          icon={StatisticsIcon}
          title="Profile Statistics"
        >
          <Stats />
        </Modal>

        <button
          className="button"
          title="Status Tiers"
          onClick={() => setIsStatusTiersModalOpen(true)}
        >
          <StatusTiersIcon />
        </button>

        <Modal
          isOpen={isStatusTiersModalOpen}
          onClose={() => setIsStatusTiersModalOpen(false)}
          icon={StatusTiersIconSrc}
          title="Status Tiers"
          className="status-tiers-modal"
        >
          <StatusTiers />
        </Modal>

        <button
          className="button"
          title="Finance History"
          onClick={() => setIsFinanceHistoryModalOpen(true)}
        >
          <CaseIcon />
        </button>

        <Modal
          isOpen={isFinanceHistoryModalOpen}
          onClose={() => setIsFinanceHistoryModalOpen(false)}
          icon={CaseIconSrc}
          title="Finance History"
          className="finance-history-modal"
        >
          <FinanceHistory />
        </Modal>

        <button
          className="button"
          title="Help"
          onClick={() => setIsHelpModalOpen(true)}
        >
          <svg className="icon" width="24" height="24">
            <use href={HelpIcon} />
          </svg>
        </button>

        <Modal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
          icon={HelpIcon}
          title="Help"
        >
          <Help />
        </Modal>

        <WheelRafflesNavButton className="button" />

        <button
          className="button sound-button"
          onClick={toggleSound}
          title={settings.sound_enabled ? "Disable sound" : "Enable sound"}
        >
          <img
            src={
              settings.sound_enabled
                ? "/assets/svg/sound.svg"
                : "/assets/svg/sound-mute.svg"
            }
            alt="Sound icon"
          />
        </button>
      </div>

      <div className="panel main-panel">
        {activeTab === "chart" && (
          <PriceChart
            tradingPanelVisible={isTradingPanelVisible}
            onOpenTradingPanel={toggleTradingPanel}
          />
        )}
        {activeTab === "profile" && <Stats />}
        {activeTab === "orders" && <Orders />}
        {activeTab === "help" && <Help withPromos />}
      </div>

      {activeTab === "chart" && isTradingPanelVisible && (
        <TradingPanel
          title="Trading Panel"
          onButtonToggle={toggleTradingPanel}
        />
      )}

      {isOrdersOpen && (
        <div className="panel orders-panel">
          <Orders />
        </div>
      )}

      <div className="vertical-panel">
        <button
          className={`button ${isOrdersOpen ? "active" : ""}`}
          title="Orders"
          onClick={() => setIsOrdersOpen(!isOrdersOpen)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.07881 5.06891C8.87414 1.27893 15.0438 1.31923 18.8623 5.13778C22.6825 8.95797 22.7212 15.1313 18.9263 18.9262C15.1314 22.7211 8.95805 22.6824 5.13786 18.8622C2.87401 16.5984 1.93916 13.5099 2.34059 10.5812C2.39684 10.1708 2.77512 9.88377 3.18549 9.94002C3.59587 9.99627 3.88295 10.3745 3.8267 10.7849C3.48672 13.2652 4.27794 15.881 6.19852 17.8016C9.443 21.0461 14.6665 21.0646 17.8656 17.8655C21.0647 14.6664 21.0461 9.44292 17.8017 6.19844C14.5588 2.95561 9.33901 2.93539 6.13947 6.12957L6.88717 6.13333C7.30138 6.13541 7.63547 6.47288 7.63339 6.88709C7.63131 7.3013 7.29384 7.63539 6.87963 7.63331L4.33408 7.62052C3.92281 7.61845 3.58993 7.28556 3.58786 6.8743L3.57507 4.32874C3.57299 3.91454 3.90708 3.57707 4.32129 3.57498C4.7355 3.5729 5.07297 3.907 5.07505 4.32121L5.07881 5.06891ZM12 7.24992C12.4142 7.24992 12.75 7.58571 12.75 7.99992V11.6893L15.0303 13.9696C15.3232 14.2625 15.3232 14.7374 15.0303 15.0302C14.7374 15.3231 14.2626 15.3231 13.9697 15.0302L11.25 12.3106V7.99992C11.25 7.58571 11.5858 7.24992 12 7.24992Z"
            />
          </svg>
        </button>
      </div>

      <BottomPanel activeTab={activeTab} setActiveTab={setActiveTab} />

      <Modal
        isOpen={!!tradingError}
        onClose={() => dispatch(setTradingError(null))}
        title="Error"
        autoCloseMs={3000}
      >
        <p className="error-text">{tradingError}</p>
      </Modal>
    </div>
  );
};

export default TradingDashboard;
